// IndexedDB wrapper for practice reports, error notebook, and score cache (NFR-7/NFR-8).

import type { PracticeReport, MusicXMLScore } from '../types';

const DB_NAME = 'violinlab';
const DB_VERSION = 1;
const STORE_REPORTS = 'reports';
const STORE_SCORES = 'scores'; // cached MusicXML (最近 3 份)

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_REPORTS)) {
        const s = db.createObjectStore(STORE_REPORTS, { keyPath: 'id' });
        s.createIndex('started_at', 'started_at');
        s.createIndex('score_id', 'score_id');
      }
      if (!db.objectStoreNames.contains(STORE_SCORES)) {
        db.createObjectStore(STORE_SCORES, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---- Reports / error notebook ----
export function saveReport(report: PracticeReport): Promise<void> {
  return tx(STORE_REPORTS, 'readwrite', (s) => s.put(report)).then(() => undefined);
}

export function listReports(limit = 100): Promise<PracticeReport[]> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const idx = db.transaction(STORE_REPORTS, 'readonly').objectStore(STORE_REPORTS).index('started_at');
        const out: PracticeReport[] = [];
        const req = idx.openCursor(null, 'prev');
        req.onsuccess = () => {
          const c = req.result;
          if (c && out.length < limit) {
            out.push(c.value as PracticeReport);
            c.continue();
          } else {
            resolve(out);
          }
        };
        req.onerror = () => reject(req.error);
      }),
  );
}

export function deleteReport(id: string): Promise<void> {
  return tx(STORE_REPORTS, 'readwrite', (s) => s.delete(id)).then(() => undefined);
}

// ---- Score cache (NFR-7: cache 最近 3 份) ----
export interface CachedScore {
  id: string;
  title: string;
  storedAt: number;
  xmlText: string;
  score: MusicXMLScore;
}

export async function cacheScore(entry: CachedScore): Promise<void> {
  await tx(STORE_SCORES, 'readwrite', (s) => s.put(entry));
  // Enforce cap of 3 most recent.
  const all = await listCachedScores();
  if (all.length > 3) {
    const toRemove = all.slice(3);
    for (const e of toRemove) await tx(STORE_SCORES, 'readwrite', (s) => s.delete(e.id));
  }
}

export function listCachedScores(): Promise<CachedScore[]> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const store = db.transaction(STORE_SCORES, 'readonly').objectStore(STORE_SCORES);
        const out: CachedScore[] = [];
        const req = store.openCursor();
        req.onsuccess = () => {
          const c = req.result;
          if (c) {
            out.push(c.value as CachedScore);
            c.continue();
          } else {
            out.sort((a, b) => b.storedAt - a.storedAt);
            resolve(out);
          }
        };
        req.onerror = () => reject(req.error);
      }),
  );
}

export async function exportAllData(): Promise<string> {
  const reports = await listReports(10000);
  const scores = await listCachedScores();
  return JSON.stringify({ exportedAt: Date.now(), reports, scores }, null, 2);
}
